import React, { FC } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { 
  BsSpeedometer2, 
  BsFlag, 
  BsPersonHeart, 
  BsSignpostSplit, 
  BsStar,
  BsChevronLeft,
  BsChevronRight
} from 'react-icons/bs';
import { TbBrandBooking } from 'react-icons/tb';
import { AiOutlineTags } from 'react-icons/ai';
import { 
  HiOutlineBell,
  HiOutlineGlobeAlt as MdOutlineTour,
  HiOutlineXMark as MdClose 
} from 'react-icons/hi2';
import {
  MdLogout,
  MdOutlineMarkEmailRead,
  MdOutlineForest,
  MdHotel,
  MdExplore,
  MdOutlineManageAccounts,
  MdOutlineHistoryToggleOff,
} from 'react-icons/md';
import { LuNewspaper } from 'react-icons/lu';
import { FaRegQuestionCircle } from 'react-icons/fa';
import { RiFileCopyLine } from 'react-icons/ri';
import { logout } from '../../../store/slices/authSlice';
import { useCountries } from '../../../hooks';
import { DAV_ROLES } from '../../../constants';
import { useDashboard } from '../../../context';
import Tooltip from '../../ui/Tooltip/Tooltip';

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  to: string;
  collapsed?: boolean;
  onClick?: () => void;
}

const NavItem: FC<NavItemProps> = ({ icon, label, to, collapsed, onClick }) => (
  <Tooltip content={label} enabled={collapsed}>
    <NavLink
      to={to}
      onClick={onClick}
      className={({ isActive }) => `
        group relative flex items-center gap-3 px-3 py-2.5 rounded-xl
        transition-all duration-200 ease-out
        ${isActive ? 
          'bg-gradient-to-r from-primary-500 to-primary-600 text-white shadow-lg shadow-primary-500/25' 
          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'
        }
        ${collapsed ? 'justify-center' : ''}
      `}
    >
      <span className='text-xl flex-shrink-0'>{icon}</span>
      {!collapsed && (
        <span className='font-medium text-sm truncate'>{label}</span>
      )}
    </NavLink>
  </Tooltip>
);

const ModernSidebar: FC = () => {
  const user = useSelector((state: any) => state.auth.user);
  const { countries, loading: countryLoading } = useCountries();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sidebarOpen, setSidebarOpen, sidebarCollapsed, setSidebarCollapsed } = useDashboard();

  const handleLogout = () => {
    dispatch(logout());
    navigate('/');
  };

  const handleNavClick = () => {
    // Close sidebar on mobile after navigation
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const { SUPER_ADMIN, ADMIN, TOUR_OPERATOR, CONTENT_MANAGER, AGENT } = DAV_ROLES;

  const MENU_ITEMS = [
    {
      title: 'Dashboard',
      link: 'overview',
      icon: <BsSpeedometer2 />,
      roles: [SUPER_ADMIN, ADMIN, AGENT],
    },
    {
      title: 'Bookings',
      link: 'bookings',
      icon: <TbBrandBooking />,
      roles: [SUPER_ADMIN],
    },
    {
      title: 'Inquiries',
      link: 'inquiries',
      icon: <FaRegQuestionCircle />,
      roles: [SUPER_ADMIN],
    },
    {
      title: 'Quotes',
      link: 'quotes',
      icon: <LuNewspaper />,
      roles: [SUPER_ADMIN],
    },
    {
      title: 'Tours',
      link: 'tours',
      icon: <MdOutlineTour />,
      roles: [SUPER_ADMIN, ADMIN, TOUR_OPERATOR],
    },
    {
      title: 'Safari Updates',
      link: 'safari-updates',
      icon: <BsSignpostSplit />,
      roles: [SUPER_ADMIN, ADMIN, CONTENT_MANAGER],
    },
    {
      title: 'Reviews',
      link: 'reviews',
      icon: <BsStar />,
      roles: [SUPER_ADMIN],
    },
    {
      title: 'Countries',
      link: 'countries',
      icon: <BsFlag />,
      roles: [SUPER_ADMIN, ADMIN, CONTENT_MANAGER],
    },
    {
      title: 'Categories',
      link: 'categories',
      icon: <AiOutlineTags />,
      roles: [SUPER_ADMIN, ADMIN, CONTENT_MANAGER],
    },
    {
      title: 'National Parks',
      link: 'national-parks',
      icon: <MdOutlineForest />,
      roles: [SUPER_ADMIN, ADMIN, CONTENT_MANAGER],
    },
    {
      title: 'Accommodations',
      link: 'accommodations',
      icon: <MdHotel />,
      roles: [SUPER_ADMIN, ADMIN, CONTENT_MANAGER],
    },
    {
      title: 'Explore',
      link: 'places',
      icon: <MdExplore />,
      roles: [SUPER_ADMIN, ADMIN, CONTENT_MANAGER],
    },
    {
      title: 'Agents',
      link: 'agents',
      icon: <BsPersonHeart />,
      roles: [SUPER_ADMIN, ADMIN],
    },
    {
      title: 'Staff',
      link: 'staff',
      icon: <MdOutlineManageAccounts />,
      roles: [SUPER_ADMIN],
    },
    {
      title: 'Notifications',
      link: 'notifications',
      icon: <HiOutlineBell />,
      roles: [SUPER_ADMIN, ADMIN, TOUR_OPERATOR, CONTENT_MANAGER],
    },
    {
      title: 'Subscribers',
      link: 'subscribers',
      icon: <MdOutlineMarkEmailRead />,
      roles: [SUPER_ADMIN, ADMIN],
    },
    {
      title: 'Pages',
      link: 'pages',
      icon: <RiFileCopyLine />,
      roles: [SUPER_ADMIN, ADMIN, CONTENT_MANAGER],
    },
    {
      title: 'Landing Pages',
      link: 'landing-pages',
      icon: <RiFileCopyLine />,
      roles: [SUPER_ADMIN, ADMIN, CONTENT_MANAGER],
    },
    {
      title: 'Audit Log',
      link: 'audit-log',
      icon: <MdOutlineHistoryToggleOff />,
      roles: [SUPER_ADMIN],
    },
  ];

  const filteredMenuItems = MENU_ITEMS.filter((item) => 
    item.roles.includes(user?.role)
  );

  return (
    <aside
      className={`
        fixed top-0 left-0 z-50 h-screen lg:h-[100dvh]
        bg-white dark:bg-gray-900
        border-r border-gray-200 dark:border-gray-800
        transition-all duration-300 ease-in-out
        flex flex-col overflow-hidden
        ${sidebarCollapsed ? 'w-20' : 'w-72'}
        ${sidebarOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full lg:translate-x-0'}
      `}
    >
      {/* Header */}
      <div className={`
        flex items-center h-[72px] px-4 flex-shrink-0
        border-b border-gray-100 dark:border-gray-800
        ${sidebarCollapsed ? 'justify-center' : 'justify-between'}
      `}>
        {!sidebarCollapsed && (
          <div className='flex items-center gap-3'>
            <img 
              src='/assets/Dav-safaris-transparent-logo.png' 
              alt='Dav Safaris Home' 
              className='h-12 w-auto'
            />
          </div>
        )}
        
        {sidebarCollapsed && (
          <img 
            src='/assets/Dav-safaris-transparent-logo.png' 
            alt='Dav Safaris Logo' 
            className='h-10 w-auto'
          />
        )}

        {/* Close button (mobile) */}
        <button
          type='button'
          onClick={() => setSidebarOpen(false)}
          className='lg:hidden p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors'
        >
          <MdClose className='w-6 h-6' />
        </button>

        {/* Collapse button (desktop) */}
        {!sidebarCollapsed && (
          <button
            type='button'
            onClick={() => setSidebarCollapsed(true)}
            className='
              hidden lg:flex items-center justify-center w-8 h-8 rounded-xl
              bg-gray-50 dark:bg-gray-800 text-gray-400
              hover:bg-primary-50 dark:hover:bg-primary-500/10 hover:text-primary-600 dark:hover:text-primary-400
              border border-gray-100 dark:border-gray-700 hover:border-primary-200 dark:hover:border-primary-500/30
              transition-all duration-300 group/collapse
            '
          >
            <BsChevronLeft className='w-4 h-4 group-hover/collapse:-translate-x-0.5 transition-transform' />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className='flex-1 overflow-y-auto overflow-x-visible py-4 px-3 space-y-1 sidebar-scroll'>
        {/* State Indicators */}
        {countryLoading && user?.role === DAV_ROLES.AGENT && (
          <div className='animate-pulse space-y-2'>
            {[1, 2, 3].map((i) => (
              <div key={i} className='h-12 bg-gray-100 dark:bg-gray-800/50 rounded-xl' />
            ))}
          </div>
        )}

        {/* Agent countries */}
        {user?.role === DAV_ROLES.AGENT && !countryLoading && (
          countries?.map((country: any) => (
            <NavItem
              key={country.id}
              icon={<img src={country.flag} alt={country.name} className='w-6 h-4 rounded-sm object-cover shadow-sm' />}
              label={country.name}
              to={`/dashboard/tours/${country.slug}`}
              collapsed={sidebarCollapsed}
              onClick={handleNavClick}
            />
          ))
        )}

        {/* Regular menu items */}
        {user?.role !== DAV_ROLES.AGENT && filteredMenuItems.map((item) => (
          <NavItem
            key={item.link}
            icon={item.icon}
            label={item.title}
            to={`/dashboard/${item.link}`}
            collapsed={sidebarCollapsed}
            onClick={handleNavClick}
          />
        ))}
      </nav>

      {/* Footer */}
      <div className='p-3 border-t border-gray-100 dark:border-gray-800 flex-shrink-0'>
        {/* Expand button when collapsed */}
        {sidebarCollapsed && (
          <Tooltip content='Expand Sidebar'>
            <button
              type='button'
              onClick={() => setSidebarCollapsed(false)}
              className='
                w-10 h-10 mx-auto mb-4 rounded-xl flex items-center justify-center
                bg-primary-500/10 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400
                hover:bg-primary-500 hover:text-white
                shadow-sm hover:shadow-lg hover:shadow-primary-500/40
                transition-all duration-300 group/expand
              '
            >
              <BsChevronRight className='w-4 h-4 group-hover/expand:translate-x-0.5 transition-transform' />
            </button>
          </Tooltip>
        )}

        {/* User info */}
        {!sidebarCollapsed && (
          <div className='flex items-center gap-3 p-3 mb-2 rounded-xl bg-gray-50 dark:bg-gray-800/50 group'>
            <div className='w-10 h-10 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-sm shadow-md group-hover:scale-105 transition-transform'>
              {user?.username?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className='flex-1 min-w-0'>
              <p className='text-sm font-bold text-gray-900 dark:text-white truncate'>
                {user?.username || 'User'}
              </p>
              <p className='text-[11px] text-gray-500 dark:text-gray-400 font-medium capitalize'>
                {user?.role?.replace('_', ' ') || 'Member'}
              </p>
            </div>
          </div>
        )}

        {/* Logout button */}
        <Tooltip content='Log Out' enabled={sidebarCollapsed}>
          <button
            type='button'
            onClick={handleLogout}
            className={`
              w-full flex items-center gap-3 px-3 py-2.5 rounded-xl
              bg-red-50 dark:bg-red-500/10 
              text-red-600 dark:text-red-400
              hover:bg-red-100 dark:hover:bg-red-500/20
              transition-all duration-200
              ${sidebarCollapsed ? 'justify-center' : ''}
            `}
          >
            <MdLogout className='text-xl' />
            {!sidebarCollapsed && <span className='font-bold text-sm'>Log Out</span>}
          </button>
        </Tooltip>
      </div>
    </aside>
  );
};

export default ModernSidebar;
